import { Suspense, lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorFallback } from './components/ErrorFallback';
import { Login } from './pages/Login';
import { Callback } from './pages/Callback';
import { LinkPopupCallback } from './pages/LinkPopupCallback';
import { PlaidOAuthReturn } from './pages/PlaidOAuthReturn';
// The index route (`/`) — ordinarily Dashboard, but redirects out of the advisor's own
// firm workspace (steward-app#700). See HomeRoute's own header. Dashboard itself stays
// eager (via HomeRoute's own import): it is the default landing surface, and
// lazy-loading it would add a fetch round trip to every first paint. Every other page
// loads on demand so its code (and its heavier deps: dnd-kit on Documents,
// react-markdown on Ask, visx via Portfolio) stays out of the entry chunk.
import { HomeRoute } from './HomeRoute';
import { RequireAdmin } from './components/RequireAdmin';
import { RequireCashFlowAccess } from './components/RequireCashFlowAccess';
import { RequireAdvisorFirm } from './advisor/RequireAdvisorFirm';
import { RequireFamilyWorkspace } from './advisor/RequireFamilyWorkspace';
import { RequireAskWorkspace } from './assistant/RequireAskWorkspace';
import { RequireFirmAdmin } from './advisor/RequireFirmAdmin';
import { RedirectFirmAdminToFirmPage } from './advisor/RedirectFirmAdminToFirmPage';
import { LegacyRedirect } from './components/LegacyRedirect';
import { WorkspaceProvider } from './workspace/WorkspaceProvider';
import { StepUpProvider } from './mfa/StepUpProvider';
import { EnrollmentGate } from './mfa/EnrollmentGate';
import { AssuranceFloorGate } from './mfa/AssuranceFloorGate';
import { SponsoredSetupFinish } from './workspace/useSponsoredSetupFinish';
import { FirstRunGate } from './onboarding/FirstRunGate';
import { TermsGate } from './terms/TermsGate';
import { OnboardingClientGate, OnboardingInShell } from './onboarding/client/OnboardingClientGate';

const Documents          = lazy(() => import('./pages/Documents').then(m => ({ default: m.Documents })));
const Assets             = lazy(() => import('./pages/Assets').then(m => ({ default: m.Assets })));
const AssetDetail        = lazy(() => import('./pages/AssetDetail').then(m => ({ default: m.AssetDetail })));
const Accounts           = lazy(() => import('./pages/Accounts').then(m => ({ default: m.Accounts })));
const AccountDetail      = lazy(() => import('./pages/AccountDetail').then(m => ({ default: m.AccountDetail })));
const CashFlow           = lazy(() => import('./pages/CashFlow').then(m => ({ default: m.CashFlow })));
const CashFlowEntity     = lazy(() => import('./pages/CashFlowEntity').then(m => ({ default: m.CashFlowEntity })));
const Entities           = lazy(() => import('./pages/Entities').then(m => ({ default: m.Entities })));
const EntityDetail       = lazy(() => import('./pages/EntityDetail').then(m => ({ default: m.EntityDetail })));
const PeopleLayout       = lazy(() => import('./pages/PeopleLayout').then(m => ({ default: m.PeopleLayout })));
const People             = lazy(() => import('./pages/People').then(m => ({ default: m.People })));
const PersonDetail       = lazy(() => import('./pages/PersonDetail').then(m => ({ default: m.PersonDetail })));
const Contacts           = lazy(() => import('./pages/Contacts').then(m => ({ default: m.Contacts })));
const ContactDetail      = lazy(() => import('./pages/ContactDetail').then(m => ({ default: m.ContactDetail })));
const Credentials        = lazy(() => import('./pages/Credentials').then(m => ({ default: m.Credentials })));
const Ask                = lazy(() => import('./pages/Ask').then(m => ({ default: m.Ask })));
const Settings           = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const SettingsIndex      = lazy(() => import('./pages/Settings').then(m => ({ default: m.SettingsIndex })));
const SettingsProfile     = lazy(() => import('./pages/settings/Profile').then(m => ({ default: m.SettingsProfile })));
const SettingsSignInKeys  = lazy(() => import('./pages/settings/SignInKeys').then(m => ({ default: m.SettingsSignInKeys })));
const SettingsPreferences = lazy(() => import('./pages/settings/Preferences').then(m => ({ default: m.SettingsPreferences })));
const SettingsConnections = lazy(() => import('./pages/settings/Connections').then(m => ({ default: m.SettingsConnections })));
const PeopleAccess       = lazy(() => import('./pages/people/Access').then(m => ({ default: m.PeopleAccess })));
const SettingsBilling     = lazy(() => import('./pages/settings/Billing').then(m => ({ default: m.SettingsBilling })));
const SettingsCashFlow    = lazy(() => import('./pages/settings/CashFlow').then(m => ({ default: m.SettingsCashFlow })));
const SettingsWorkspace   = lazy(() => import('./pages/settings/Workspace').then(m => ({ default: m.SettingsWorkspace })));
const PeopleAdvisors      = lazy(() => import('./pages/people/Advisors').then(m => ({ default: m.SettingsAdvisors })));
const Portfolio          = lazy(() => import('./pages/Portfolio').then(m => ({ default: m.Portfolio })));
const PortfolioLayout    = lazy(() => import('./pages/PortfolioLayout').then(m => ({ default: m.PortfolioLayout })));
const Liabilities        = lazy(() => import('./pages/Liabilities').then(m => ({ default: m.Liabilities })));
const LiabilityDetail    = lazy(() => import('./pages/LiabilityDetail').then(m => ({ default: m.LiabilityDetail })));
const SelectWorkspace    = lazy(() => import('./pages/SelectWorkspace').then(m => ({ default: m.SelectWorkspace })));
const Announcements      = lazy(() => import('./pages/Announcements').then(m => ({ default: m.Announcements })));
const Feedback           = lazy(() => import('./pages/Feedback').then(m => ({ default: m.Feedback })));
// The firm's snapshot form (docs/25 §3). Outside `ProtectedRoute`, and lazy like every other
// non-landing surface, so its code is fetched only by a visit to `/intake`. That is a
// statement about the bundle and not about who visits: the form's own pages past the first
// two are signed in (`intake/IntakeFlow.tsx`), and nothing here stops a person already signed
// in to the app from opening `/intake`. `intake/intakeBundleImports.test.ts` is what limits
// what this chunk may import.
const IntakePage         = lazy(() => import('./intake/IntakePage').then(m => ({ default: m.IntakePage })));
// The advisor portal (docs/25 §2, S7). Lazy like every non-landing surface: an
// ordinary family workspace never loads this code. The nav offers it only to firm
// members (useAdvisorFirm), and the routes below are wrapped in `RequireAdvisorFirm`
// (`/advisor`, `/advisor/clients/...`) or `RequireFirmAdmin` (`/advisor/firm/...`), which
// refuse a direct URL from a caller with no advisor firm (or, for Firm, who is not a firm
// admin; see each guard's header for the loading and error states). Those wrappers are UX gating:
// what a caller can actually READ is appcore's membership check on each request, same
// as every other page in this router.
const AdvisorClients     = lazy(() => import('./advisor/AdvisorClients').then(m => ({ default: m.AdvisorClients })));
const AdvisorClientsLayout = lazy(() => import('./advisor/AdvisorClientsLayout').then(m => ({ default: m.AdvisorClientsLayout })));
const AdvisorOverview    = lazy(() => import('./advisor/AdvisorOverview').then(m => ({ default: m.AdvisorOverview })));
const AdvisorIntakes     = lazy(() => import('./advisor/AdvisorIntakes').then(m => ({ default: m.AdvisorIntakes })));
// steward-app#1763: one client-record profile page replaces the three former detail routes
// (`IntakeDetail`/`ProspectDraftDetail`/`IntakeInvitationDetail`, all deleted) — a submission,
// a live saved draft and a bare invitation are three shapes of the same prospect, and they
// now share one URL keyed on the durable `advisor-client` id rather than three unrelated ones.
const ClientProfile      = lazy(() => import('./advisor/ClientProfile').then(m => ({ default: m.ClientProfile })));
const YourFirm           = lazy(() => import('./advisor/YourFirm').then(m => ({ default: m.YourFirm })));
const FirmDetailsTab     = lazy(() => import('./advisor/YourFirm').then(m => ({ default: m.FirmDetailsTab })));
const FirmPeopleTab      = lazy(() => import('./advisor/YourFirm').then(m => ({ default: m.FirmPeopleTab })));
const FirmClientSharingTab = lazy(() => import('./advisor/FirmClientSharingTab').then(m => ({ default: m.FirmClientSharingTab })));

/**
 * Wraps a lazy page in the shared route-level Suspense boundary. The fallback
 * mirrors the app's quiet inline loading style (no spinner theater): the Layout
 * chrome stays mounted, only the page body swaps.
 */
function page(Component: LazyExoticComponent<ComponentType>) {
  return (
    <Suspense fallback={<div className="p-6 text-slate text-[13px] font-sans">Loading…</div>}>
      <Component />
    </Suspense>
  );
}

/**
 * The route table, exported separately from {@link router} so `routeGuards.test.tsx` can mount
 * THIS table in a memory router and assert which guards wrap which path (steward-app#1546,
 * #1921, #1922). A `MemoryRouter` rebuilt in a test proves only that a guard honours its
 * props; it stays green when a route is moved out from under its wrapper here.
 */
const allRoutes: RouteObject[] = [
  {
    // Pathless wrapper so a single errorElement catches an unhandled throw
    // anywhere in the app — /login and /callback included — instead of
    // falling through to react-router's own default error page (which
    // renders a raw stack trace straight to the user).
    element: <Outlet />,
    errorElement: <ErrorFallback />,
    children: [
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/callback',
        element: <Callback />,
      },
      {
        // The advisor-firm client intake (docs/25 §3). Outside ProtectedRoute on purpose,
        // alongside /login, and outside the workspace chrome entirely: the people who fill
        // this in are prospective clients of the firm, who arrive with no login and no
        // workspace (the submission becomes an entity in the firm's). Since
        // crossmarc/steward-app#2158 the route is public only as far as the cover and the
        // basics page: the form itself asks the person to sign in (making a login if they
        // have none) and to pass a second check, and does that itself
        // (`intake/IntakeFlow.tsx`) rather than through ProtectedRoute, which would send them
        // to the app's own login page and workspace picker.
        //
        // Two paths, one page: `/intake/{firmSlug}` on the shared app host, and
        // `/intake` on a firm's branded host, which resolves the firm by hostname.
        path: '/intake',
        children: [
          { index: true, element: page(IntakePage) },
          { path: ':firmSlug', element: page(IntakePage) },
        ],
      },
      {
        // The Google link-ceremony popup's redirect target (W1.5). Runs inside
        // the popup window only, driven by its own isolated OIDC session
        // (src/auth/linkPopup.ts) — never the main tab's auth state, so this
        // deliberately sits outside ProtectedRoute (no access-token check: the
        // popup's job is just to relay its callback URL back to the opener).
        // Path must match crossmarc-auth's deployed Cognito app-client callbackUrls
        // exactly (deploy/apps/steward.json / app-client.ts) — a mismatch here is a
        // live redirect_uri rejection, not a graceful fallback.
        path: '/link-callback',
        element: <LinkPopupCallback />,
      },
      {
        // Plaid OAuth return target (Charles Schwab etc.). Authenticated but deliberately
        // OUTSIDE the workspace Layout/WorkspaceProvider chrome: it only resumes Link and
        // hands off to /settings/connections. Registered in the Plaid dashboard as an
        // Allowed redirect URI (wildcard host `https://*.steward.crossmarc.com/plaid-oauth`).
        path: '/plaid-oauth',
        element: <ProtectedRoute><PlaidOAuthReturn /></ProtectedRoute>,
      },
      {
        // The onboarding screens for a person who ALREADY has a workspace (steward-app#2465), mounted as
        // a sibling of the shell route so `Layout` does not draw a second header around the form's own
        // page. The gate chain is the shell's, in the same order and with the same AssuranceFloorGate
        // and SponsoredSetupFinish (a floored workspace gets its wall here too, and the sponsored
        // finish still runs), with an `<Outlet />` where `Layout` is. `OnboardingInShell.test.tsx` pins this
        // chain (the census in `routeGuards.test.tsx` reads only the shell route's). `OnboardingClientGate` still takes over for the no-workspace case
        // before any of this renders, and `OnboardingInShell` sends a person with no invitation home.
        path: '/onboarding',
        element: <ProtectedRoute><StepUpProvider><FirstRunGate><OnboardingClientGate><EnrollmentGate><WorkspaceProvider><TermsGate><AssuranceFloorGate><SponsoredSetupFinish /><Outlet /></AssuranceFloorGate></TermsGate></WorkspaceProvider></EnrollmentGate></OnboardingClientGate></FirstRunGate></StepUpProvider></ProtectedRoute>,
        children: [
          { index: true, element: <OnboardingInShell /> },
          { path: 'form', element: <OnboardingInShell /> },
          { path: 'done', element: <OnboardingInShell /> },
          { path: 'requests', element: <OnboardingInShell /> },
        ],
      },
      {
        path: '/',
        // StepUpProvider sits above EnrollmentGate so the step-up ceremony UI
        // (api/stepUp.ts's registered controller) is available even for the
        // enrollment flow itself (adding a 2nd+ factor can trip the MED-2
        // step-up gate) and for Settings → Sign-in keys management, not just
        // ordinary workspace-scoped reads. FirstRunGate sits above
        // EnrollmentGate — both are above WorkspaceProvider because
        // /me/factors and /me/provision are identity-scoped, not
        // workspace-scoped — first-run onboarding (W1.11) and enrollment must
        // both work before a workspace exists.
        //
        // AssuranceFloorGate is the innermost gate, INSIDE WorkspaceProvider
        // (it reads the active membership's floor) and OUTSIDE Layout (it holds
        // the whole shell, chrome included, not just the page body). It must
        // stay below StepUpProvider so the wall it waits for can paint over it.
        //
        // SponsoredSetupFinish (steward-app#1488) sits beside Layout INSIDE AssuranceFloorGate for the
        // same reason: a sponsored workspace can be floored from creation, and its page-load finish
        // must run after the wall, not be refused before it. It renders nothing.
        //
        // OnboardingClientGate (steward-app#1764) sits between FirstRunGate and EnrollmentGate. A
        // client a firm invited straight to onboarding, with no workspace, gets the onboarding
        // screens INSTEAD of everything below it, so never reaches WorkspaceProvider. For anyone
        // else it renders its children. Someone who already has a workspace and was then invited to onboarding or
        // sent requests (#2465) reaches the same screens through the sibling `/onboarding` route
        // below, which has these same gates but no Layout.
        element: <ProtectedRoute><StepUpProvider><FirstRunGate><OnboardingClientGate><EnrollmentGate><WorkspaceProvider><TermsGate><AssuranceFloorGate><SponsoredSetupFinish /><Layout /></AssuranceFloorGate></TermsGate></WorkspaceProvider></EnrollmentGate></OnboardingClientGate></FirstRunGate></StepUpProvider></ProtectedRoute>,
        children: [
          { index: true, element: <HomeRoute />, handle: { crumb: 'Overview' } },
          { path: 'select-workspace', element: page(SelectWorkspace), handle: { crumb: 'Workspaces' } },
          // Layout routes (no element) render their child via the implicit <Outlet/>.
          // Their `handle.crumb` builds the breadcrumb trail (src/components/Breadcrumbs.tsx).
          {
            path: 'documents',
            handle: { crumb: 'Documents' },
            children: [
              { index: true,    element: page(Documents) },
              { path: ':id',    element: page(Documents) }, // detail appends a dynamic crumb (the title)
            ],
          },
          {
            path: 'portfolio',
            // Shell carrying the pillar's tab bar (#578) — every surface below renders
            // into its Outlet, so the bar is present on the sub-pages too, which is the
            // half the header buttons it replaced never covered.
            element: page(PortfolioLayout),
            handle: { crumb: 'Portfolio' }, // /portfolio = the Portfolio (net-worth) view
            children: [
              // No crumb here: the index view IS "Portfolio", so the trail/title/tab
              // stay just "Portfolio" rather than "Portfolio › Statement".
              { index: true, element: page(Portfolio) },
              {
                path: 'accounts',
                handle: { crumb: 'Accounts' },
                children: [
                  { index: true, element: page(Accounts) },
                  { path: ':id', element: page(AccountDetail) }, // account landing (dynamic crumb)
                ],
              },
              {
                path: 'assets',
                handle: { crumb: 'Assets' },
                children: [
                  { index: true, element: page(Assets) },
                  { path: 'asset/:id', element: page(AssetDetail) },     // asset landing (dynamic crumb)
                ],
              },
              {
                path: 'liabilities',
                handle: { crumb: 'Liabilities' },
                children: [
                  { index: true, element: page(Liabilities) },
                  { path: ':id', element: page(LiabilityDetail) }, // liability landing (dynamic crumb)
                ],
              },
            ],
          },
          {
            // Cash flow v0 (steward-app#2592). The board is the landing page (slice 14). Turning on, accounts,
            // history and levels live in Settings → Cash flow (steward-app#2792); `setup` redirects there.
            // Guards: a family workspace only (`RequireFamilyWorkspace`), and a firm advisor who is
            // not an admin is sent home (`RequireCashFlowAccess`). The nav item is capability-gated
            // (`useCanReadCashFlow`) and every `/cash-flow/*` call is gated by appcore.
            element: <RequireFamilyWorkspace to="/" />,
            children: [
              {
                element: <RequireCashFlowAccess />,
                children: [
                  {
                    path: 'cash-flow',
                    handle: { crumb: 'Cash flow' },
                    children: [
                      { index: true, element: page(CashFlow) },
                      { path: 'entity/:key', element: page(CashFlowEntity) }, // dynamic crumb (the entity's name)
                      // steward-app#2792: Setup moved, whole, to Settings → Cash flow. A redirect rather than a
                      // 404, because the path is on dev and in links already sent. The target carries its
                      // own guards (RequireAdmin, RequireFamilyWorkspace), so a non-admin ends on Settings.
                      { path: 'setup', element: <LegacyRedirect to="/settings/cash-flow" /> },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'entities',
            handle: { crumb: 'Entities' },
            children: [
              { index: true, element: page(Entities) },
              { path: ':id', element: page(EntityDetail) }, // entity landing (dynamic crumb)
            ],
          },
          {
            // The People pillar (steward-app#1425, docs/04-navigation-ia.md §3.1).
            //
            // `PeopleLayout` carries the tab bar — Family | Contacts — the same shape as
            // `PortfolioLayout` and `AdvisorClientsLayout`, so it is present on the roster,
            // on Contacts, AND on both detail pages.
            //
            // ⚠️ **Contacts folded in here; it is not a sibling pillar any more, and there is
            // NO `/contacts` redirect.** Pre-launch, no external users, and §3.2's own note
            // records that the legacy redirects were removed for exactly that reason, so the
            // referencing files were updated instead of the URL being preserved.
            //
            // ⚠️ The static `contacts` child MUST out-rank `:personId`. react-router ranks a
            // static segment above a dynamic one regardless of declaration order, so this is
            // structural rather than a matter of keeping the lines in this order — but the
            // façade's own `PAGE_ROUTES` table matches first-wins and does depend on order
            // there. Its comment says so.
            //
            // ⚠️ `:personId`, not `:id`, and it is deliberate: this route's page context is
            // excused in `pageContextRoutes.test.ts` because the assistant handler does not
            // fetch the roster, and the parameter name is what makes that exemption visible
            // rather than silent. The crumb it contributes is the person's name, via
            // `useDynamicCrumb`, exactly like every other detail page.
            path: 'people',
            element: page(PeopleLayout),
            handle: { crumb: 'People' },
            children: [
              // No crumb on the index: the roster IS "People", so the trail stays one word
              // rather than reading "People › Family" — the same call `PortfolioLayout`'s
              // index makes.
              { index: true, element: page(People) },
              { path: ':personId', element: page(PersonDetail) }, // person landing (dynamic crumb)
              {
                // steward-app#1921 — the login lifecycle (who can sign in, their role, pending
                // invitations), moved here from Settings → Members. Its guards moved WITH it,
                // unchanged and in the same order: `RequireAdmin` (a non-admin is bounced to
                // `/settings`, as before), then `RedirectFirmAdminToFirmPage` (a workspace not
                // KNOWN to be a family's goes to the Firm page, which is where a firm's own
                // people are managed, #1729/#1742). The tab half is `PeopleLayout`, on the same
                // predicate. `routeGuards.test.tsx` binds this path to both guards, in order.
                //
                // Static `access` out-ranks `:personId` for the same structural reason as
                // `contacts` below (see this route's header); the façade's `PAGE_ROUTES` is the
                // order-dependent one.
                element: <RequireAdmin />,
                children: [
                  {
                    element: <RedirectFirmAdminToFirmPage />,
                    children: [
                      { path: 'access', element: page(PeopleAccess), handle: { crumb: 'Access' } },
                    ],
                  },
                ],
              },
              {
                path: 'contacts',
                // No static crumb (steward-app#1932): the tab's name depends on the caller
                // ("Contacts", or "Other contacts" when Advisors is offered), so `Contacts` sets it
                // as a dynamic crumb and `ContactDetail` sets its own parent crumb the same way.
                children: [
                  { index: true,  element: page(Contacts) },
                  { path: ':id',  element: page(ContactDetail) }, // contact landing (dynamic crumb)
                ],
              },
              {
                // People → Advisors (steward-app#1922): the advisor-firm channel (docs/25
                // §1/§2, S6), moved WHOLE from Settings → Advisors behind exactly the guards
                // it had there. `RequireAdmin` is a courtesy that mirrors appcore's own
                // membership/capability checks, never a substitute for them (see
                // handlers/advisor.ts's header). `RequireFamilyWorkspace` (steward-app#798):
                // the tab that connects a family to an advisory firm makes no sense from
                // inside the firm's own workspace — see that guard's header. Both redirect to
                // `/settings`, as they did when this page lived there. `routeGuards.test.tsx`
                // renders THIS table and pins the binding in both directions (#1546).
                //
                // The static `advisors` segment out-ranks `:personId` above whatever the
                // array order: React Router scores a static segment over a dynamic one at the
                // same depth, and pathless wrappers do not change a branch's path.
                element: <RequireAdmin />,
                children: [
                  {
                    element: <RequireFamilyWorkspace />,
                    children: [
                      { path: 'advisors', element: page(PeopleAdvisors), handle: { crumb: 'Advisors' } },
                      // steward-app#1932: one page per connected FIRM, `:firmKey` being the
                      // connection's `firmId` (the same key `DELETE /advisor/connections/{firmId}`
                      // and the shared-areas routes use). The SAME component as the list: the
                      // list and the firm page share one set of queries, handlers and dialogs, and
                      // `useParams` says which to draw. A sibling of `advisors`, not a child, so it
                      // sits under the very same two guards above and needs no guard of its own.
                      // No static crumb: the page contributes People > Advisors > {firm name}
                      // through `useDynamicCrumb`, like every other detail page.
                      { path: 'advisors/:firmKey', element: page(PeopleAdvisors) },
                    ],
                  },
                ],
              },
            ],
          },
          { path: 'credentials', element: page(Credentials), handle: { crumb: 'Credentials' } },
          {
            // The advisor portal (docs/25 §2, S7): a client-workspace card list and
            // the firm's intake submissions. UX-gated in the nav (useAdvisorFirm),
            // never here — see that hook's header. Every read is membership-checked by
            // appcore per request. The one page below that WRITES is `firm` (Your firm,
            // steward-app#1361): member roles, invitations and accepting a waiting family,
            // each authorized by appcore, behind a firm-admin UX gate of its own.
            path: 'advisor',
            // No crumb on this wrapper: Overview, Clients and Firm are siblings, each its
            // own top-level advisor surface, and a shared crumb here would make one
            // read as nested under another in the breadcrumb trail (they aren't — see
            // the per-child crumbs below).
            children: [
              {
                // Resolves useAdvisorFirm() ONCE for the whole subtree — see
                // RequireAdvisorFirm's own header for why a route wrapper (mirroring
                // RequireAdmin below) rather than a check each page repeats.
                //
                // 🚨 **`firm` is NOT nested under this element (steward-app#1742).** It used
                // to be, but `RequireAdvisorFirm` refuses outright (a hard EmptyState) the
                // moment `advisorFor` is null — while it is still loading, or after a failed
                // lookup — which is exactly the window `RequireFirmAdmin` needs to be
                // MOUNTED to render its own loading/retry state instead. Nesting `firm` here
                // meant that window never reached it at all: a firm admin whose lookup had
                // not resolved or had failed saw "This area is for advisory-firm team
                // members" (RequireAdvisorFirm's refusal) rather than anything about their
                // own Firm page. `firm` is a sibling below with its own independent gate
                // (`RequireFirmAdmin`) for exactly this reason — Overview and Clients stay
                // here because their `advisorFor!` non-null assertions genuinely depend on
                // this wrapper having already refused a null one.
                element: <RequireAdvisorFirm />,
                children: [
                  {
                    // The firm Overview (steward-app#738, docs/26 §2a) — the
                    // `/advisor` INDEX since 2026-09-11 (it shipped dark behind
                    // `advisorOverviewEnabled()`, now removed; see this page's own
                    // header). Clients moved to `/advisor/clients` below.
                    index: true,
                    element: page(AdvisorOverview),
                    handle: { crumb: 'Overview' },
                  },
                  {
                    // "Clients" folds in Intake submissions as a "Prospects" tab
                    // (steward-app#1429): a prospect's stage ladder derives from an intake
                    // draft, when no workspace exists yet, and the client's own onboarding
                    // rungs (#1426, not yet built) derive from a workspace — one journey for
                    // one family crosses that seam, which is why it is one pillar with two
                    // tabs rather than two separate rail items. `AdvisorClientsLayout`
                    // carries the tab bar (matching 'portfolio' below), crumb "Clients" on
                    // the wrapper so it reads the same whichever tab is open — same pattern
                    // as 'portfolio' (crumb "Portfolio" == its index view's own name).
                    path: 'clients',
                    element: page(AdvisorClientsLayout),
                    handle: { crumb: 'Clients' },
                    children: [
                      { index: true, element: page(AdvisorClients) },
                      {
                        // Formerly the sibling 'intakes' route (`/advisor/intakes`), folded
                        // in here by #1429. No redirect: pre-launch, no external users (see
                        // docs/04-navigation-ia.md's legacy-route note), so the ~22 files that
                        // referenced the old path were updated instead of preserved.
                        path: 'prospects',
                        handle: { crumb: 'Prospects' },
                        children: [
                          { index: true, element: page(AdvisorIntakes) },
                          // steward-app#1763: the three former detail routes that used to live
                          // here (`drafts/:draftId`, `invitations/:id`, `:id`) are gone. Every
                          // one of them opened a single artifact belonging to one prospect —
                          // exactly what `advisor-client` now names directly — so they collapsed
                          // into `ClientProfile`, below, keyed on the durable client id instead
                          // of a submission/draft/invitation id that changed as the prospect
                          // moved through the funnel.
                          //
                          // The one exception: a submission or draft with NO `clientId` (never
                          // stamped, e.g. every submission from before #1773) has no profile URL,
                          // so the same page opens it here by its own id. A row that does carry
                          // a `clientId` redirects to `:clientId` above; see ClientProfile.tsx.
                          { path: ':rowId', element: page(ClientProfile) },
                        ],
                      },
                      // 🚨 AFTER `prospects` and BEFORE nothing else needs ordering here:
                      // React Router ranks a static segment ('prospects') above a dynamic one
                      // (':clientId') at the same tree level regardless of array order, so
                      // `/advisor/clients/prospects` can never be swallowed by this route — see
                      // the (removed) `drafts/:draftId` comment above for the same principle.
                      // One profile page for every stage of the client-record journey
                      // (crossmarc/steward-app#1763): a prospect filling in the Snapshot, one
                      // mid-onboarding, and an active client are all `advisor-client` rows and
                      // now share this one URL shape.
                      { path: ':clientId', element: page(ClientProfile) },
                    ],
                  },
                ],
              },
              {
                // The firm admin's page (steward-app#1361). A SIBLING of the
                // `RequireAdvisorFirm` subtree above, not nested under it (steward-app#1742
                // — see that element's own comment for why). `RequireFirmAdmin` is its own,
                // independent gate: UX gating in the same sense as `RequireAdmin` below, but
                // unlike that element it does not simply admit-or-refuse — it renders a
                // loading state, a "which firm" chooser, a retry/mismatch state, or the real
                // page, entirely on its own. appcore's authorizer decides every call the page
                // makes regardless. See `src/advisor/firmAdmin.ts` and `RequireFirmAdmin.tsx`.
                path: 'firm',
                handle: { crumb: 'Firm' },
                element: <RequireFirmAdmin />,
                children: [
                  {
                    // The tab shell (steward-app#1970): page intro, the firm-lookup notice and the tab
                    // bar. Pathless, so both tabs sit under `RequireFirmAdmin` above and a hand-typed
                    // `/advisor/firm/people` is refused exactly as `/advisor/firm` is;
                    // `routeGuards.test.tsx` pins that. No crumb on the index, like People's: the
                    // trail stays "Firm" rather than "Firm > Details".
                    element: page(YourFirm),
                    children: [
                      { index: true, element: page(FirmDetailsTab) },
                      { path: 'people', element: page(FirmPeopleTab), handle: { crumb: 'People' } },
                      // steward-app#1971. The crumb is the tab's label.
                      { path: 'sharing', element: page(FirmClientSharingTab), handle: { crumb: 'Client sharing' } },
                    ],
                  },
                ],
              },
            ],
          },
          { path: 'announcements', element: page(Announcements), handle: { crumb: 'Announcements' } },
          { path: 'feedback', element: page(Feedback), handle: { crumb: 'Feedback' } },
          {
            // Defensive route guard, for a bookmarked or hand-typed `/ask` URL — the
            // primary defense is `AskControl` (Layout.tsx) not offering the top-bar
            // entry point in the firm's own workspace (steward-app#1541). `to="/"`
            // rather than the `RequireFamilyWorkspace` default of `/settings`: this
            // guard has no Settings tab to fall back to, and `HomeRoute` already
            // sends a firm workspace on from `/` to `/advisor` or
            // `FirmWorkspaceNotReady` — see that guard's own header. Since steward-app#1815
            // the guard admits the firm workspace too while `firmAssistantEnabled()` is on
            // (dev only); see `RequireAskWorkspace`.
            element: <RequireAskWorkspace />,
            children: [{ path: 'ask', element: page(Ask), handle: { crumb: 'Ask' } }],
          },
          {
            path: 'settings',
            element: page(Settings),
            handle: { crumb: 'Settings' },
            children: [
              // Index lands everyone on Profile (SettingsIndex).
              { index: true, element: page(SettingsIndex) },
              { path: 'profile', element: page(SettingsProfile), handle: { crumb: 'Profile' } },
              { path: 'sign-in-keys', element: page(SettingsSignInKeys), handle: { crumb: 'Security' } },
              // Logins merged into the page above as a section (docs/29 F4). The path
              // stays live as a redirect rather than being deleted: it shipped to dev as
              // a real tab, so bookmarks and any link already sent exist in the wild, and
              // a 404 is a worse answer than the page the content actually moved to. The
              // env gate now lives on the SECTION, so no guard is needed here — the
              // redirect target is correct whether or not social login is on.
              { path: 'logins', element: <LegacyRedirect to="/settings/sign-in-keys" /> },
              // steward-app#1921 — Members moved to People → Access. A permanent redirect, and
              // deliberately OUTSIDE `RequireAdmin` below: the destination carries the same two
              // guards, so gating here too would only decide the same thing twice. A non-admin
              // still ends on `/settings` and a firm workspace on `/advisor/firm`, exactly as a
              // bookmarked `/settings/members` did before; `routeGuards.test.tsx` pins both.
              { path: 'members', element: <LegacyRedirect to="/people/access" /> },
              // Advisors moved, whole, to People → Advisors (steward-app#1922). 🚨 The redirect
              // MUST carry the query: prospects already hold connect links minted as
              // `/settings/advisors?advisor=<advisorId>` (InviteClientDialog.tsx, before the
              // move), and a redirect that dropped `?advisor=` would land them on the page with
              // the firm silently lost (compare steward-app#1643). `LegacyRedirect` carries
              // `search` and `hash`; `routeGuards.test.tsx` pins it with a real value. Unguarded
              // on purpose, like `logins`: the TARGET carries the guards, so a non-admin or a
              // firm workspace ends where it did before the move (`/settings`).
              { path: 'advisors', element: <LegacyRedirect to="/people/advisors" /> },
              { path: 'preferences', element: page(SettingsPreferences), handle: { crumb: 'Preferences' } },
              {
                // Connections is NOT admin-gated (see ConnectedAccounts.tsx's own header:
                // a read-only member keeps read access on purpose) — only the
                // workspace-kind guard applies, so this sits OUTSIDE RequireAdmin below,
                // unlike Workspace. Defensive route half of steward-app#808: the tab itself
                // (Settings.tsx) is the primary defense, this guard is for a bookmarked or
                // hand-typed `/settings/connections` URL. `RequireFamilyWorkspace`,
                // written generically in steward-app#798/#806 for exactly this kind of
                // second consumer.
                element: <RequireFamilyWorkspace />,
                children: [
                  { path: 'connections', element: page(SettingsConnections), handle: { crumb: 'Connections' } },
                ],
              },
              // Admin-only tabs — guarded so non-admins deep-linking bounce to /settings.
              {
                element: <RequireAdmin />,
                children: [
                  {
                    // steward-app#1730 — no Workspace tab (so no Rename or Delete) in the
                    // firm's own workspace; the firm's name is edited on the Firm page. Route
                    // half of the tab being hidden in Settings.tsx, for a bookmarked or
                    // hand-typed `/settings/workspace`, exactly like People → Advisors.
                    element: <RequireFamilyWorkspace />,
                    children: [
                      { path: 'workspace', element: page(SettingsWorkspace), handle: { crumb: 'Workspace' } },
                      { path: 'billing', element: page(SettingsBilling), handle: { crumb: 'Billing' } },
                      // steward-app#2792: cash flow is a family feature, admin-managed. Same guards as Workspace.
                      { path: 'cash-flow', element: page(SettingsCashFlow), handle: { crumb: 'Cash flow' } },
                    ],
                  },
                ],
              },
            ],
          },
          // The founder-invite admin console moved out of this customer app into the
          // internal crossmarc-ops operator console (crossmarc-ops docs/10).
          // Unknown paths land on the Dashboard (no legacy URLs to preserve pre-launch).
          { path: '*',        element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
];

export const routes: RouteObject[] = allRoutes;

export const router = createBrowserRouter(routes);
